<template>
    <!-- 最外层父级容器：整页唯一滚动容器，高度=可视区、滚动区间=内容高度，footer 之后不会再有空白 -->
    <div ref="downloadPage" class="download-page" @scroll.passive="onPageScroll">
        <!-- 顶部操作栏：选择学校 / 导出图片，不参与截图 -->
        <div class="download-toolbar">
            <div class="download-toolbar-school" @click="openSchoolPopup">
                已选{{ selectedSchoolIds.length }}所学校
                <icon class="arrow" name="arrow-down" />
            </div>
            <button class="download-toolbar-export" :disabled="exporting || !canExport"
                @click="exportImage">导出图片</button>
        </div>
        <!-- 数据筛选：筛选后重新请求数据，不影响已选学校范围 -->
        <div class="download-filters">
            <checkbox :model-value="includeLightCourseActivity" shape="square" icon-size="0.28rem"
                :disabled="loading || exporting"
                @update:model-value="updateFilter('includeLightCourseActivity', $event)">
                包含仅轻课/活动
            </checkbox>
            <checkbox :model-value="deduplicateSameName" shape="square" icon-size="0.28rem"
                :disabled="loading || exporting" @update:model-value="updateFilter('deduplicateSameName', $event)">
                同班同名去重
            </checkbox>
        </div>
        <div class="download-index" id="download-capture">
            <!-- 头部：活动信息 + 概览 -->
            <div class="download-header">
                <div class="download-header-title">{{ displayData.title }}</div>
                <div class="download-header-time">数据统计时间：{{ displayData.statistics_time }}</div>
                <div class="download-header-overview">
                    <div class="download-header-overview-one">
                        <div class="num">{{ overview.personCount }}</div>
                        <div class="label">参与人数</div>
                    </div>
                    <div class="download-header-overview-one">
                        <div class="num">{{ overview.schoolCount }}</div>
                        <div class="label">参与学校</div>
                    </div>
                    <div class="download-header-overview-one">
                        <div class="num">{{ overview.classCount }}</div>
                        <div class="label">参与班级</div>
                    </div>
                </div>
            </div>
            <!-- 学校数据 -->
            <div class="download-body">
                <div class="download-school-card" v-for="school in displayedSchools" :key="school.school_id">
                    <div class="download-school-card-head">
                        <div class="name">{{ school.school_name || school.name }}</div>
                        <div class="person">
                            <span class="num">{{ school.course_person_count }}</span>
                            <span class="unit">人</span>
                        </div>
                    </div>
                    <div class="download-school-card-desc" v-if="school.course_person_count > 0">{{ school.grade_count
                        }}个年级 {{ school.class_count }}个班级</div>
                    <!-- 年级表格：一行最多7列（班级列+6个年级），超出换行展示；默认超过20个班级收起，可展开 -->
                    <table class="download-grade-table" v-if="school.course_person_count > 0"
                        v-for="(chunk, ci) in chunkGrades(school.list)" :key="ci">
                        <thead>
                            <tr class="table-row table-head">
                                <th class="cell cell-class">班级</th>
                                <th class="cell" v-for="grade in chunk" :key="grade.name">{{ grade.name }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="table-row" v-for="row in visibleClassRows(school.school_id, chunk, ci)"
                                :key="row">
                                <td class="cell cell-class">{{ row }}</td>
                                <td class="cell" v-for="grade in chunk" :key="grade.name">{{ cellValue(grade, row) }}
                                </td>
                            </tr>
                            <tr v-if="shouldShowToggle(school.school_id, chunk, ci)" class="table-row table-toggle">
                                <td class="cell toggle-cell" :colspan="chunk.length + 1"
                                    @click="toggleTableExpand(school.school_id, ci)">
                                    <span v-if="tableExpandingKey === tableKey(school.school_id, ci)"
                                        class="download-spin download-spin--small"></span>
                                    <template v-else>
                                        <span>{{ isTableExpanded(school.school_id, ci) ? '收起' : '展开全部' }}</span>
                                        <icon class="toggle-arrow"
                                            :name="isTableExpanded(school.school_id, ci) ? 'arrow-up' : 'arrow-down'" />
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="table-row table-total">
                                <td class="cell cell-class">合计</td>
                                <td class="cell" v-for="grade in chunk" :key="grade.name">{{ cellValue(grade, '合计') }}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <div class="download-empty" v-if="!displayedSchools.length">暂无学校数据</div>
                <div class="download-school-more" v-if="displayedSchools.length && !exporting">
                    <template v-if="schoolListLoading">
                        <span class="download-spin download-spin--small"></span>
                        <span class="download-more-text">加载中...</span>
                    </template>
                    <span v-else-if="!hasMoreSchools">已展示全部学校</span>
                </div>
                <div class="download-footer">小盒·教得更好，学得更好</div>
            </div>
        </div>
    </div>
    <button v-if="showBackToTop && !exporting && !showSchoolPopup" class="download-back-to-top" type="button"
        aria-label="回到顶部" @click.stop.prevent="scrollToTop" @touchstart.stop.prevent="scrollToTop">
        <icon name="back-top" />
    </button>
    <!-- 导出截图专用层：与日常展示完全分离，只在导出时渲染到视口外，避免 html2canvas 受日常列表滚动/状态干扰 -->
    <div v-if="exporting" class="download-export-capture-wrapper">
        <download-export-capture id="download-export-capture" :displayData="displayData"
            :selectedSchools="selectedSchools" :overview="overview" />
    </div>
    <!-- 选择学校弹框 -->
    <popup v-if="schoolPopupMounted" v-model:show="showSchoolPopup" position="bottom" round :style="{ height: '70%' }"
        @closed="onSchoolPopupClosed">
        <div class="school-popup">
            <div class="school-popup-title">
                <span>选择学校</span>
                <span class="close" @click="closeSchoolPopup">×</span>
            </div>
            <div class="school-popup-search">
                <search v-model="searchKeyword" placeholder="请输入搜索关键词" />
            </div>
            <div class="school-popup-all" @click="toggleSelectAll">
                {{ isAllSelected ? "取消选择全部" : "选择全部" }} {{ tempSelectedSchoolIds.length }}/{{ filteredSchools.length }}
            </div>
            <div ref="schoolPopupList" class="school-popup-list" @scroll.passive="loadMoreSchools">
                <div class="school-popup-item" v-for="school in visibleSchools" :key="school.school_id">
                    <checkbox :model-value="tempSelectedSchoolIdSet.has(school.school_id)" shape="round"
                        @update:model-value="toggleSchool(school.school_id, $event)">
                        {{ school.school_name || school.name }}
                    </checkbox>
                </div>
                <div class="school-popup-empty" v-if="!filteredSchools.length">无匹配学校</div>
            </div>
            <div class="school-popup-confirm" @click="confirmSelect">确定</div>
        </div>
    </popup>
</template>
<script lang="ts">
import { onMounted, ref, computed, nextTick, watch, onBeforeUnmount } from "vue";
import Toast from "vant/es/toast";
import "vant/es/toast/style";
import { Popup, Search, Checkbox, Icon } from "vant";
import "vant/es/popup/style";
import "vant/es/search/style";
import "vant/es/checkbox/style";
import "vant/es/icon/style";
import axios from "axios";
import html2canvas from "html2canvas";
import DownloadExportCapture from "../components/DownloadExportCapture.vue";
import '../utils/flex.js'

// 七牛上传配置（参考 lightblue-sls 项目）
const UPLOAD_URL = "https://upload.qiniup.com"; // 七牛cdn上传地址
const QINIU_URL = "https://innerplatformqiniu.knowbox.cn/"; // 七牛cdn资源拼接地址
// 年级表格一行最多7列：班级列 + 6个年级列，超出换行
const GRADE_COLUMNS_PER_ROW = 6;
// 导出图片最多包含5所学校
const MAX_EXPORT_SCHOOL_COUNT = 5;
// 选择学校弹窗列表单次渲染批次（超出后滚动到底再追加渲染）
const SCHOOL_RENDER_BATCH_SIZE = 50;
// 跳转小程序下载文件页的文件类型：image=图片（后续可扩展其他文件类型）
const DOWNLOAD_FILE_TYPE_IMAGE = "image";
// 班级行超过该数量时默认收起
const CLASS_ROWS_COLLAPSE_LIMIT = 20;
// 学校分页：每页加载数量，与导出上限保持一致
const SCHOOL_PAGE_SIZE = MAX_EXPORT_SCHOOL_COUNT;

const getQueryString = (name: string) => {
    var reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
    var r = window.location.search.substr(1).match(reg);
    if (r != null) {
        return unescape(r[2]);
    }
    return null;
};

interface ClassItem {
    name: string;
    course_person_count: number;
}

interface Grade {
    name: string;
    list: ClassItem[];
}

interface School {
    school_id: number;
    school_name?: string;
    name?: string;
    course_person_count: number;
    grade_count: number;
    class_count: number;
    list: Grade[];
}

interface Overview {
    personCount: number;
    schoolCount: number;
    classCount: number;
}

interface DisplayData {
    title: string;
    statistics_time: string;
    school_list: School[];
}

export default {
    components: {
        Popup,
        Search,
        Checkbox,
        Icon,
        DownloadExportCapture,
    },
    setup() {
        const key = getQueryString("id");
        // 登录token（由小程序webview经/data页透传），仿h5-lightblue：需登录态接口（如upload-token）放入请求头token
        const tk = getQueryString("tk") || "";
        const displayData = ref<DisplayData>({
            title: "",
            statistics_time: "",
            school_list: [],
        });
        const selectedSchoolIds = ref<number[]>([]);
        const showSchoolPopup = ref(false);
        const schoolPopupMounted = ref(false);
        const tempSelectedSchoolIds = ref<number[]>([]);
        const searchKeyword = ref("");
        const visibleSchoolCount = ref(SCHOOL_RENDER_BATCH_SIZE);
        const schoolPopupList = ref<HTMLElement | null>(null);
        const exporting = ref(false);
        const loading = ref(false);
        const includeLightCourseActivity = ref(false);
        const deduplicateSameName = ref(false);
        const downloadPage = ref<HTMLElement | null>(null);
        const showBackToTop = ref(false);

        const initData = async (selectAll = false) => {
            loading.value = true;
            Toast.loading({
                message: "加载中...",
                duration: 0,
                forbidClick: true,
                overlay: true,
                overlayClass: "download-toast-overlay",
            });
            try {
                const res: any = await axios.post(
                    "/api/activity/link-data-display",
                    {
                        key,
                        includeLightCourseActivity: Number(includeLightCourseActivity.value),
                        deduplicateSameName: Number(deduplicateSameName.value),
                    }
                );
                if (res.data.code !== 0) {
                    Toast(res.data.msg || "数据获取失败");
                    return false;
                }
                displayData.value = res.data.data;
                if (selectAll) {
                    // 首次进入默认选中全部学校
                    selectedSchoolIds.value = displayData.value.school_list.map((s: School) => s.school_id);
                }
                Toast.clear();
                return true;
            } catch (error) {
                console.log(error);
                Toast("数据获取失败，请稍后重试");
                return false;
            } finally {
                loading.value = false;
            }
        };
        // 切换筛选：先更新选中态并重新拉取数据，失败时回滚选中态
        const updateFilter = async (name: "includeLightCourseActivity" | "deduplicateSameName", checked: boolean) => {
            if (loading.value || exporting.value) return;
            const filter = name === "includeLightCourseActivity" ? includeLightCourseActivity : deduplicateSameName;
            const previousValue = filter.value;
            filter.value = checked;
            if (!await initData()) {
                filter.value = previousValue;
            }
        };

        const selectedSchoolIdSet = computed(() => new Set(selectedSchoolIds.value));
        // 选中的学校列表
        const selectedSchools = computed<School[]>(() => {
            return (displayData.value.school_list || []).filter((s: School) =>
                selectedSchoolIdSet.value.has(s.school_id)
            );
        });

        // 概览数据：当前选中学校的汇总
        const overview = computed<Overview>(() => {
            const schools = selectedSchools.value;
            return {
                personCount: schools.reduce((sum: number, s: School) => sum + s.course_person_count, 0),
                schoolCount: schools.length,
                classCount: schools.reduce((sum: number, s: School) => sum + s.class_count, 0),
            };
        });

        // 是否可导出：有学校数据且当前选中学校总参与人数大于0
        const canExport = computed(() => {
            return !loading.value && !!(displayData.value.school_list || []).length && overview.value.personCount > 0;
        });

        // 选择学校弹框
        const openSchoolPopup = async () => {
            // 数据加载/导出过程中禁止打开弹窗，避免弹窗内的提示与全局 loading（vant Toast 单例）互相覆盖
            if (loading.value || exporting.value) return;
            tempSelectedSchoolIds.value = [...selectedSchoolIds.value];
            searchKeyword.value = "";
            visibleSchoolCount.value = SCHOOL_RENDER_BATCH_SIZE;
            schoolPopupMounted.value = true;
            await nextTick();
            showSchoolPopup.value = true;
            await nextTick();
            if (schoolPopupList.value) {
                schoolPopupList.value.scrollTop = 0;
            }
        };
        const closeSchoolPopup = () => {
            showSchoolPopup.value = false;
        };
        const onSchoolPopupClosed = () => {
            if (!showSchoolPopup.value) {
                schoolPopupMounted.value = false;
            }
        };
        const filteredSchools = computed<School[]>(() => {
            const keyword = (searchKeyword.value || "").trim();
            if (!keyword) return displayData.value.school_list || [];
            return (displayData.value.school_list || []).filter((s: School) =>
                (s.school_name || s.name || "").includes(keyword)
            );
        });
        const tempSelectedSchoolIdSet = computed(() => new Set(tempSelectedSchoolIds.value));
        const visibleSchools = computed<School[]>(() =>
            filteredSchools.value.slice(0, visibleSchoolCount.value)
        );
        const resetVisibleSchools = () => {
            visibleSchoolCount.value = SCHOOL_RENDER_BATCH_SIZE;
            nextTick(() => {
                if (schoolPopupList.value) {
                    schoolPopupList.value.scrollTop = 0;
                }
            });
        };
        const loadMoreSchools = () => {
            const list = schoolPopupList.value;
            if (
                !list ||
                visibleSchoolCount.value >= filteredSchools.value.length ||
                list.scrollTop + list.clientHeight < list.scrollHeight - 1
            ) {
                return;
            }
            visibleSchoolCount.value = Math.min(
                visibleSchoolCount.value + SCHOOL_RENDER_BATCH_SIZE,
                filteredSchools.value.length
            );
        };
        const toggleSchool = (schoolId: number, checked: boolean) => {
            const selectedIds = new Set(tempSelectedSchoolIds.value);
            if (checked) {
                selectedIds.add(schoolId);
            } else {
                selectedIds.delete(schoolId);
            }
            tempSelectedSchoolIds.value = Array.from(selectedIds);
        };
        watch(searchKeyword, resetVisibleSchools);
        const toggleSelectAll = () => {
            // 作用范围为当前展示的学校列表（无搜索时为全部，搜索时为搜索结果）
            const rangeIds = filteredSchools.value.map((s: School) => s.school_id);
            const selectedIds = new Set(tempSelectedSchoolIds.value);
            if (isAllSelected.value) {
                rangeIds.forEach((id: number) => selectedIds.delete(id));
            } else {
                rangeIds.forEach((id: number) => selectedIds.add(id));
            }
            tempSelectedSchoolIds.value = Array.from(selectedIds);
        };
        // 是否已选中当前展示范围（全部学校或搜索结果）内的全部学校（决定「选择全部/取消选择全部」文案）
        const isAllSelected = computed(() => {
            const range = filteredSchools.value;
            const selectedIds = tempSelectedSchoolIdSet.value;
            return range.length > 0 && range.every((s: School) =>
                selectedIds.has(s.school_id)
            );
        });
        const confirmSelect = () => {
            // 至少选择一所学校；提示前先清掉可能残存的导出 loading，避免提示被「图片上传中」覆盖
            Toast.clear();
            if (!tempSelectedSchoolIds.value.length) {
                Toast("至少选择一所学校");
                return;
            }
            selectedSchoolIds.value = [...tempSelectedSchoolIds.value];
            closeSchoolPopup();
        };

        // iOS 键盘弹起时 WKWebView 会把文档（html/body，overflow:hidden 只能被程序化滚动）顶起一段来
        // “露出”输入框，而选择学校弹框是 fixed 定位、不随文档滚动移动，因此只有背景被顶上去；
        // 关闭弹框后这个文档级偏移不会自动还原，用户也无法用触摸滚回去，表现为“背景被顶起且回不到顶部”。
        // 这里在弹框打开期间监听文档滚动并立即复位：复位只影响普通流中的背景，fixed 弹框与输入框位置不变。
        const resetDocumentScroll = () => {
            const docEl = document.documentElement;
            const body = document.body;
            if (window.pageYOffset || docEl.scrollTop || body.scrollTop) {
                window.scrollTo(0, 0);
                docEl.scrollTop = 0;
                body.scrollTop = 0;
            }
        };
        let removeDocScrollListenerTimer = 0;
        watch(showSchoolPopup, (show: boolean) => {
            if (show) {
                window.clearTimeout(removeDocScrollListenerTimer);
                window.addEventListener("scroll", resetDocumentScroll, { passive: true });
                return;
            }
            // 关闭弹框：立即复位一次，并延迟摘掉监听（覆盖键盘收起动画期间 iOS 再次顶起文档），
            // 摘除前再兜底复位一次，确保最终停在顶部
            resetDocumentScroll();
            window.clearTimeout(removeDocScrollListenerTimer);
            removeDocScrollListenerTimer = window.setTimeout(() => {
                resetDocumentScroll();
                window.removeEventListener("scroll", resetDocumentScroll);
            }, 600);
        });
        onBeforeUnmount(() => {
            window.clearTimeout(removeDocScrollListenerTimer);
            window.removeEventListener("scroll", resetDocumentScroll);
        });

        const gradeOrder = (name: string) => {
            const match = name.match(/\d+|[一二三四五六七八九十]+/);
            if (!match) return Infinity;
            const chineseNumbers = ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二"];
            const number = Number(match[0]) || chineseNumbers.indexOf(match[0]) + 1;
            const offset = name.startsWith("初") ? 6 : name.startsWith("高") ? 9 : 0;
            return number ? number + offset : Infinity;
        };
        // 年级列分块：每块最多6个年级（加班级列共7列），超出换到下一块表格展示
        const chunkGrades = (gradeList: Grade[]) => {
            const chunks: Grade[][] = [];
            const list = [...(gradeList || [])].sort((a, b) => gradeOrder(a.name) - gradeOrder(b.name));
            for (let i = 0; i < list.length; i += GRADE_COLUMNS_PER_ROW) {
                chunks.push(list.slice(i, i + GRADE_COLUMNS_PER_ROW));
            }
            return chunks.length ? chunks : [[]];
        };
        // 班级行顺序：全局按班级号从小到大（不按年级分组，跨年级相同班级号去重）
        // 例：一年级 2000~2002、二年级 1998~2001 → 班级号排序为 1998、1999、2000、2001、2002
        const classRows = (chunk: Grade[]) => {
            const names = new Set<string>();
            (chunk || []).forEach((grade: Grade) => {
                (grade.list || [])
                    .filter((cls: ClassItem) => cls.name !== "合计")
                    .forEach((cls: ClassItem) => names.add(cls.name));
            });
            return Array.from(names).sort((a: string, b: string) =>
                a.localeCompare(b, "zh-Hans-CN", { numeric: true })
            );
        };
        // 单元格取值：某年级下对应班级的参与人数
        const cellValue = (grade: Grade, rowName: string) => {
            const cls = (grade.list || []).find((c: ClassItem) => c.name === rowName);
            return cls ? cls.course_person_count : "-";
        };

        // 表格展开/收起：默认收起，超过 CLASS_ROWS_COLLAPSE_LIMIT 行显示展开按钮；手风琴，同时只展开一个表格
        const expandedTableKey = ref<string | null>(null);
        const tableExpandingKey = ref<string | null>(null);
        const tableKey = (schoolId: number, chunkIndex: number) => `${schoolId}-${chunkIndex}`;
        const isTableExpanded = (schoolId: number, chunkIndex: number) => {
            return expandedTableKey.value === tableKey(schoolId, chunkIndex);
        };
        const shouldShowToggle = (schoolId: number, chunk: Grade[], chunkIndex: number) => {
            return classRows(chunk).length > CLASS_ROWS_COLLAPSE_LIMIT;
        };
        const visibleClassRows = (schoolId: number, chunk: Grade[], chunkIndex: number) => {
            const rows = classRows(chunk);
            if (isTableExpanded(schoolId, chunkIndex)) {
                return rows;
            }
            return rows.slice(0, CLASS_ROWS_COLLAPSE_LIMIT);
        };
        const toggleTableExpand = (schoolId: number, chunkIndex: number) => {
            const key = tableKey(schoolId, chunkIndex);
            const expanded = isTableExpanded(schoolId, chunkIndex);
            // 先展示 loading，下一帧再真正切换 DOM，避免大量行渲染阻塞导致 loading 不显示
            tableExpandingKey.value = key;
            requestAnimationFrame(() => {
                nextTick(() => {
                    expandedTableKey.value = expanded ? null : key;
                    tableExpandingKey.value = null;
                });
            });
        };

        // 学校分页：默认展示一页，滚动接近底部时自动加载下一页
        const displayedSchoolCount = ref(0);
        const schoolListLoading = ref(false);
        const displayedSchools = computed<School[]>(() => {
            return selectedSchools.value.slice(0, displayedSchoolCount.value);
        });
        const hasMoreSchools = computed(() => {
            return displayedSchoolCount.value < selectedSchools.value.length;
        });
        const loadMoreDisplayedSchools = async () => {
            if (schoolListLoading.value || !hasMoreSchools.value) return;
            schoolListLoading.value = true;
            // 小延迟让 loading 样式先渲染出来，提升上拉感知
            await new Promise(resolve => setTimeout(resolve, 300));
            displayedSchoolCount.value = Math.min(
                displayedSchoolCount.value + SCHOOL_PAGE_SIZE,
                selectedSchools.value.length
            );
            schoolListLoading.value = false;
        };
        watch(selectedSchools, () => {
            // 学校选择或数据变化后重置分页与收起状态
            displayedSchoolCount.value = Math.min(SCHOOL_PAGE_SIZE, selectedSchools.value.length);
            expandedTableKey.value = null;
        });

        const onPageScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            const scrollTopValue = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            // 滚动超过 300px 显示回到顶部
            showBackToTop.value = scrollTopValue > 300;
            // 距离底部不足 100px 时自动加载下一页
            if (scrollHeight - scrollTopValue - target.clientHeight < 100) {
                loadMoreDisplayedSchools();
            }
        };
        const scrollToTop = () => {
            downloadPage.value?.scrollTo({ top: 0, behavior: "smooth" });
        };

        // 上传图片至七牛（参考 h5-lightblue / lightblue-sls 的上传逻辑：token接口需携带登录态请求头）
        const uploadImage = async (file: Blob, fileName: string) => {
            const tokenRes: any = await axios.post(
                "/api/common/upload-token",
                {},
                {
                    headers: {
                        token: tk,
                    },
                }
            );
            if (tokenRes.data.code !== 0) {
                Toast(tokenRes.data.msg || "获取七牛云token失败，请重试");
                return "";
            }
            const formData = new FormData();
            formData.append("key", `lightblue-link/${fileName}`);
            formData.append("fname", fileName);
            formData.append("file", file, fileName);
            formData.append("token", tokenRes.data.data.upload_token);
            const res: any = await axios.post(UPLOAD_URL, formData);
            return QINIU_URL + res.data.key;
        };

        // 导出图片：html2canvas 分片截图 + 上传七牛
        const exportImage = async () => {
            if (exporting.value || !canExport.value) {
                return;
            }
            // 选中学校数量超过上限时，提示后中止导出
            if (selectedSchools.value.length > MAX_EXPORT_SCHOOL_COUNT) {
                Toast(`最多支持导出${MAX_EXPORT_SCHOOL_COUNT}所学校`);
                return;
            }
            exporting.value = true;
            const toast = Toast.loading({
                message: "图片生成中...",
                duration: 0,
                forbidClick: true,
                overlay: true,
                overlayClass: "download-toast-overlay",
            });
            // 导出前收起所有表格，减少 html2canvas 每次克隆整页 DOM 的体积
            expandedTableKey.value = null;
            await nextTick();
            // 等一帧让浏览器完成布局，再读取导出层的 scrollWidth/scrollHeight 作为分片依据
            await new Promise<void>(resolve => {
                requestAnimationFrame(() => {
                    resolve();
                });
            });
            try {
                const target = document.getElementById("download-export-capture");
                if (!target) {
                    throw new Error("未找到截图区域");
                }
                const contentWidth = target.scrollWidth;
                const contentHeight = target.scrollHeight;
                if (!contentWidth || !contentHeight) {
                    throw new Error("截图区域尺寸异常");
                }
                // iOS webview 对超大 canvas 会静默失败：单片栅格化丢图、或整图 toBlob 返回 null。
                // 策略：分片渲染（同时控制单片面积和单边长度）+ 自动降 scale 重试，优先保证导出成功。
                const MAX_SLICE_AREA = 6_000_000;
                const MIN_SCALE = 0.25;
                const totalArea = contentWidth * contentHeight;

                const generateBlobAtScale = async (scale: number): Promise<Blob | null> => {
                    const MAX_SLICE_DIM = 6000;
                    const sliceHeightCss = Math.max(
                        1000,
                        Math.min(
                            Math.floor(MAX_SLICE_AREA / (contentWidth * scale * scale)),
                            Math.floor(MAX_SLICE_DIM / scale)
                        )
                    );
                    const finalCanvas = document.createElement("canvas");
                    finalCanvas.width = Math.floor(contentWidth * scale);
                    finalCanvas.height = Math.ceil(contentHeight * scale);
                    const ctx = finalCanvas.getContext("2d");
                    if (!ctx) {
                        throw new Error("画布初始化失败");
                    }
                    ctx.fillStyle = "#f2f4f9";
                    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
                    let offsetY = 0;
                    for (let y = 0; y < contentHeight; y += sliceHeightCss) {
                        const sliceHeight = Math.min(sliceHeightCss, contentHeight - y);
                        const sliceCanvas = await html2canvas(target, {
                            useCORS: true,
                            backgroundColor: "#f2f4f9",
                            logging: false,
                            scale,
                            x: 0,
                            y,
                            width: contentWidth,
                            height: sliceHeight,
                            windowWidth: contentWidth,
                            windowHeight: sliceHeight,
                        });
                        ctx.drawImage(sliceCanvas, 0, offsetY);
                        offsetY += sliceCanvas.height;
                        // 尽快释放每片的画布内存，降低 iOS 渲染后续分片时丢图的概率
                        sliceCanvas.width = 0;
                        sliceCanvas.height = 0;
                    }
                    return new Promise(resolve => finalCanvas.toBlob(resolve as any, "image/png"));
                };

                // 先用 64M 像素这档尝试，失败再按 0.75 倍降 scale 重试
                let scale = Math.min(2, Math.sqrt(64_000_000 / totalArea));
                let blob: Blob | null = null;
                while (scale >= MIN_SCALE) {
                    blob = await generateBlobAtScale(scale);
                    if (blob) break;
                    scale *= 0.75;
                }
                if (!blob) {
                    // 生成图片失败：toast提示并中断后续上传/跳转流程（finally统一复位exporting）
                    Toast.clear();
                    Toast("图片生成失败，请重试");
                    return;
                }
                const title = (displayData.value.title || "活动")
                    .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, "_").trim().slice(0, 40);
                const now = new Date();
                const timestamp = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
                    + `${String(now.getHours()).padStart(2, "0")}时${String(now.getMinutes()).padStart(2, "0")}分${String(now.getSeconds()).padStart(2, "0")}秒`;
                const fileName = `${title}_报名统计_${timestamp}.png`;
                const url = await uploadImage(blob, fileName);
                if (!url) {
                    // 上传失败：uploadImage 内部已 Toast 具体错误（如登录过期），保留提示不被清除
                    return;
                }
                Toast.clear();
                // 上传成功后不再弹额外提示，直接跳转下载文件页（页面本身即结果呈现），
                // 避免跳转转场时与「图片上传中」loading 观感重复
                const downloadInBrowser = () => {
                    const localUrl = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = localUrl;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.setTimeout(() => URL.revokeObjectURL(localUrl), 60000);
                };
                // 仿照sls流程：小程序环境跳转小程序下载文件页，由小程序保存图片
                const wxMp: any = (window as any).wx;
                if (wxMp && wxMp.miniProgram) {
                    wxMp.miniProgram.getEnv((res: any) => {
                        if (res.miniprogram) {
                            wxMp.miniProgram.navigateTo({
                                url: `/pages/download-file/index?url=${encodeURIComponent(url)}&type=${DOWNLOAD_FILE_TYPE_IMAGE}&fileName=${encodeURIComponent(fileName)}`,
                            });
                        } else {
                            downloadInBrowser();
                        }
                    });
                } else {
                    // 本地浏览器调试兜底：直接下载图片
                    downloadInBrowser();
                }
            } catch (error) {
                console.log(error);
                Toast.clear();
                // 优先透传接口返回的具体错误信息（如登录过期）
                const errRes: any = error;
                const errMsg =
                    (errRes.response && errRes.response.data && errRes.response.data.msg) || "";
                Toast(errMsg || "导出失败，请重试");
            } finally {
                exporting.value = false;
            }
        };

        onMounted(() => {
            document.title = "报名统计";
            // 首次加载的 loading 由 initData 统一展示
            initData(true);
        });

        return {
            MAX_EXPORT_SCHOOL_COUNT,
            displayData,
            loading,
            includeLightCourseActivity,
            deduplicateSameName,
            updateFilter,
            selectedSchoolIds,
            selectedSchools,
            displayedSchools,
            overview,
            showSchoolPopup,
            schoolPopupMounted,
            tempSelectedSchoolIds,
            tempSelectedSchoolIdSet,
            searchKeyword,
            visibleSchools,
            schoolPopupList,
            loadMoreSchools,
            toggleSchool,
            exporting,
            canExport,
            schoolListLoading,
            hasMoreSchools,
            downloadPage,
            showBackToTop,
            tableExpandingKey,
            openSchoolPopup,
            closeSchoolPopup,
            onSchoolPopupClosed,
            filteredSchools,
            toggleSelectAll,
            isAllSelected,
            confirmSelect,
            chunkGrades,
            classRows,
            cellValue,
            tableKey,
            isTableExpanded,
            shouldShowToggle,
            visibleClassRows,
            toggleTableExpand,
            onPageScroll,
            scrollToTop,
            exportImage,
        };
    },
};
</script>
<style lang="less">
/* 文档锁定为可视高度、自身不滚动，页面滚动交给最外层 .download-page：
   数据页残留的水印节点（absolute 挂在 body 上）无法再把文档 scrollHeight 撑大，
   从根源上消除 footer 后面多出的一大片可滚动空白 */
html,
body {
    height: 100%;
    overflow: hidden;

    /* 只对 iOS 生效：-webkit-touch-callout 仅 iOS Safari/WebView 支持，
       Android 等不支持该特性的浏览器会整块跳过，不再关闭其弹性滚动 */
    @supports (-webkit-touch-callout: none) {
        /* 关闭 iOS 文档级弹性滚动 */
        overscroll-behavior: none;
    }
}

/* 高度基准链：让 .download-index 的 % 高度能一路解析上来 */
#app {
    height: 100%;
}

/* loading 蒙层（Toast overlay）单独调浅，避免大面积深色遮住内容；不影响选择学校弹框的蒙层 */
.van-overlay.download-toast-overlay {
    background: rgba(0, 0, 0, 0.3);
}

/* 导出截图专用层：放在视口外，不参与页面布局/滚动，只在导出时渲染 */
.download-export-capture-wrapper {
    position: absolute;
    left: -9999px;
    top: 0;
    width: 7.5rem;
    z-index: -1;
}

/* 整页唯一滚动容器：高度=可视区，滚动区间=内容高度，footer 之后不会再有空白 */
.download-page {
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    background: #f2f4f9;
    overflow-y: auto;

    /* 只对 iOS 生效：该容器自身即滚动区，关闭边界回弹，避免底部被拖出空白 */
    @supports (-webkit-touch-callout: none) {
        overscroll-behavior: none;
    }

    -webkit-overflow-scrolling: touch;
    /* viewport-fit=cover 铺满到安全区后，底部让出 Home 指示条高度（旧系统不识别 env 时取 0） */
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);
}

.download-toolbar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0.24rem 0.32rem;
    background: #ffffff;

    .download-toolbar-school {
        min-width: 2.2rem;
        padding: 0.14rem 0.32rem;
        font-size: 0.26rem;
        font-family: PingFangSC-Regular, PingFang SC;
        color: #323233;
        background: #eef0f4;
        border-radius: 0.34rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        .arrow {
            font-size: 0.26rem;
            color: #969799;
            margin-left: 0.08rem;
        }

        &:active {
            opacity: 0.7;
        }
    }

    .download-toolbar-export {
        padding: 0.14rem 0.34rem;
        font-size: 0.26rem;
        font-family: PingFangSC-Medium, PingFang SC;
        font-weight: 500;
        color: #ffffff;
        background: #323233;
        border: none;
        border-radius: 0.34rem;
        cursor: pointer;

        &:disabled {
            opacity: 0.6;
        }
    }
}

.download-filters {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0.08rem 0.32rem 0.24rem;
    background: #ffffff;

    .van-checkbox__label {
        margin-left: 0.08rem;
        font-size: 0.26rem;
        line-height: 0.36rem;
        color: #646566;
    }
}

.download-back-to-top {
    position: fixed;
    right: 0.32rem;
    bottom: 0.4rem;
    bottom: calc(0.4rem + env(safe-area-inset-bottom));
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.88rem;
    height: 0.88rem;
    padding: 0;
    border: 1px solid #ebedf0;
    border-radius: 50%;
    background: #ffffff;
    color: #3f8bf2;
    box-shadow: 0 0.04rem 0.16rem rgba(0, 0, 0, 0.12);
    font-size: 0.44rem;
    cursor: pointer;
    pointer-events: auto;

    &:active {
        opacity: 0.8;
    }

    .van-icon {
        pointer-events: none;
    }
}

.download-index {
    width: 7.5rem;
    flex: 1 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* footer 距视口底部固定 16px */
    padding-bottom: 16px;
    font-size: 0.28rem;
    background: #f2f4f9;
    box-sizing: border-box;

    /* 导出截图层（DownloadExportCapture 根节点复用本类名）专用：不撑高、footer 紧贴内容，避免大片空白 */
    &.is-exporting {
        min-height: 0;

        .download-body {
            display: block;
            flex: none;
        }

        .download-school-card {
            border-radius: 0;
        }

        .download-grade-table {
            background: #ffffff;
        }

        .download-school-more {
            display: none;
        }
    }

    .download-header {
        position: relative;
        /* 蓝色区域高度，白卡中线始终落在蓝色底边上（top + 高度/2 = 该值） */
        height: 3.3rem;
        padding: 0.8rem 0.32rem 0;
        box-sizing: border-box;
        background: linear-gradient(180deg, #3f8bf2 0%, #5ea1f5 100%);

        &-title {
            font-size: 0.4rem;
            font-family: PingFangSC-Medium, PingFang SC;
            font-weight: 500;
            color: #ffffff;
            line-height: 0.66rem;
            margin-bottom: 0.12rem;
            overflow: hidden;
            /* 超出部分隐藏 */
            white-space: nowrap;
            /* 强制不换行 */
            text-overflow: ellipsis;
            /* 超出显示 ... */
        }

        &-time {
            font-size: 0.24rem;
            font-family: PingFangSC-Regular, PingFang SC;
            color: rgba(255, 255, 255, 0.8);
            line-height: 0.34rem;
            margin-bottom: 0.4rem;
        }

        &-overview {
            position: absolute;
            left: 0.32rem;
            right: 0.32rem;
            top: 2.42rem;
            height: 1.76rem;
            display: flex;
            align-items: center;
            justify-content: space-around;
            background: #ffffff;
            border-radius: 0.12rem;
            box-sizing: border-box;

            &-one {
                display: flex;
                flex-direction: column;
                align-items: center;

                .num {
                    font-size: 0.36rem;
                    font-family: PingFangSC-Medium, PingFang SC;
                    font-weight: 500;
                    color: #3f8bf2;
                    line-height: 0.44rem;
                    margin-bottom: 0.1rem;
                }

                .label {
                    font-size: 0.28rem;
                    font-family: PingFangSC-Regular, PingFang SC;
                    color: #969799;
                    line-height: 0.4rem;
                }
            }
        }
    }

    .download-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        margin: 1.1rem 0.32rem 0;
    }

    .download-school-card {
        background: #ffffff;
        border-radius: 0.12rem;
        padding: 0.3rem;
        margin-bottom: 0.2rem;

        &-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.1rem;

            .name {
                flex: 1;
                /* 占满剩余宽度 */
                min-width: 0;
                /* 允许 flex 子项收缩换行，不把右侧人数挤出去 */
                font-size: 0.3rem;
                font-family: PingFangSC-Medium, PingFang SC;
                font-weight: 500;
                color: #323233;
                line-height: 0.42rem;
                word-break: break-all;
                /* 学校名可无限换行 */
            }

            .person {
                flex-shrink: 0;
                /* 人数固定不被压缩 */
                margin-left: 0.16rem;
                display: flex;
                align-items: baseline;
                padding: 0.06rem 0;
                border-radius: 0.26rem;

                .num {
                    font-size: 0.32rem;
                    font-family: PingFangSC-Medium, PingFang SC;
                    font-weight: 500;
                    color: #323233;
                    line-height: 0.45rem;
                }

                .unit {
                    font-size: 0.22rem;
                    font-family: PingFangSC-Regular, PingFang SC;
                    color: #969799;
                    margin-left: 0.04rem;
                }
            }
        }

        &-desc {
            font-size: 0.24rem;
            font-family: PingFangSC-Regular, PingFang SC;
            color: #969799;
            line-height: 0.34rem;
            margin-bottom: 0.2rem;
        }
    }

    .download-grade-table {
        /* 原生 table 布局：班级列自动取整列最宽班名宽度且所有行对齐，任意长班名完整展示 */
        display: table;
        width: 100%;
        table-layout: auto;
        border-collapse: separate;
        border-spacing: 0;
        margin-bottom: 0.28rem;

        &:last-child {
            margin-bottom: 0;
        }

        .table-row {
            display: table-row;

            &.table-head,
            &.table-total {
                .cell {
                    background: #f7f8fa;
                    color: #323233;
                    font-family: PingFangSC-Semibold, PingFang SC;
                    font-weight: 600;
                }

                .cell:first-child {
                    border-radius: 0.08rem 0 0 0.08rem;
                }

                .cell:last-child {
                    border-radius: 0 0.08rem 0.08rem 0;
                }
            }

            &.table-total .cell {
                border-bottom: none;
            }

            &.table-toggle .toggle-cell {
                text-align: center;
                color: #3f8bf2;
                font-size: 0.24rem;
                padding: 0.14rem 0.05rem;
                cursor: pointer;
                border-bottom: 1px dashed #ebedf0;

                &:active {
                    opacity: 0.7;
                }

                .toggle-arrow {
                    display: inline-block;
                    font-size: 0.2rem;
                    margin-left: 0.06rem;
                    color: #3f8bf2;
                    vertical-align: middle;
                }
            }

            .cell {
                display: table-cell;
                vertical-align: middle;
                padding: 0.18rem 0.05rem;
                font-size: 0.24rem;
                font-family: PingFangSC-Regular, PingFang SC;
                color: #323233;
                text-align: center;
                line-height: 0.34rem;
                border-bottom: 1px dashed #ebedf0;
                /* table 布局下列宽按内容自适应，不截断不换行 */
                white-space: nowrap;

                &.cell-class {
                    /* 班级列：整列最宽班名自动撑开，单行完整展示，无省略号 */
                    text-align: left;
                    color: #646566;
                }
            }
        }
    }

    .download-school-more {
        padding: 0.3rem 0;
        text-align: center;
        font-size: 0.24rem;
        color: #969799;

        .download-more-text {
            margin-left: 0.1rem;
            vertical-align: middle;
        }

        /* 加载态使用主题蓝色 */
        .download-spin + .download-more-text,
        .download-spin {
            color: #3f8bf2;
        }
    }

    /* 自定义 loading 动画：替代 vant Loading，避免引入 vant/loading 样式导致 CSS 顺序警告 */
    .download-spin {
        display: inline-block;
        width: 0.32rem;
        height: 0.32rem;
        border: 2px solid rgba(63, 139, 242, 0.2);
        border-top-color: #3f8bf2;
        border-radius: 50%;
        animation: download-spin-rotate 0.8s linear infinite;
        vertical-align: middle;

        &--small {
            width: 0.24rem;
            height: 0.24rem;
            border-width: 1px;
        }
    }

    @keyframes download-spin-rotate {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    .download-empty {
        padding: 1rem 0;
        text-align: center;
        color: #969799;
        font-size: 0.26rem;
    }

    .download-footer {
        /* 紧跟内容之后，不用 margin-top:auto 推底，避免学校少时卡片和 footer 之间夹出大段空白 */
        padding: 0.4rem 0 0;
        text-align: center;
        font-size: 0.22rem;
        font-family: PingFangSC-Regular, PingFang SC;
        color: #646566;
    }
}

.school-popup {
    height: 100%;
    display: flex;
    flex-direction: column;
    font-size: 0.28rem;

    .school-popup-title {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.3rem;
        font-size: 0.32rem;
        font-family: PingFangSC-Medium, PingFang SC;
        font-weight: 500;
        color: #323233;

        .close {
            position: absolute;
            right: 0.3rem;
            font-size: 0.4rem;
            color: #969799;
            padding: 0 0.1rem;
            cursor: pointer;
        }
    }

    .school-popup-search {
        display: flex;
        align-items: center;
        padding: 0 0.3rem;

        .van-search {
            flex: 1;
            padding: 0.1rem 0;

            .van-search__content {
                border-radius: 0.3rem;
            }
        }
    }

    .school-popup-all {
        margin: 0.2rem 0.3rem 0;
        padding: 0.14rem 0.24rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.24rem;
        color: #3f8bf2;
        border: 1px solid #3f8bf2;
        border-radius: 0.08rem;
        cursor: pointer;
    }

    .school-popup-list {
        flex: 1;
        overflow: auto;
        margin-top: 0.2rem;
        padding: 0 0.3rem 0.3rem;

        .school-popup-item {
            padding: 0.24rem 0;
            border-bottom: 1px solid #f2f3f5;

            &:last-child {
                border-bottom: none;
            }

            .van-checkbox__label {
                font-size: 0.28rem;
                color: #323233;
                margin-left: 0.2rem;
            }
        }

        .school-popup-empty {
            padding: 0.6rem 0;
            text-align: center;
            color: #969799;
            font-size: 0.26rem;
        }
    }

    .school-popup-confirm {
        margin: 0.2rem 0.3rem 0.4rem;
        height: 0.8rem;
        line-height: 0.8rem;
        text-align: center;
        font-size: 0.3rem;
        font-family: PingFangSC-Medium, PingFang SC;
        font-weight: 500;
        color: #ffffff;
        background: #3f8bf2;
        border-radius: 0.4rem;
        cursor: pointer;
    }
}
</style>
